import FingerprintJS from '@fingerprintjs/fingerprintjs';

/**
 * Generate a device fingerprint using FingerprintJS open source library
 * Intent: Create a stable identifier for the browser/device for security purposes
 * Uses FingerprintJS which analyzes: canvas, WebGL, audio, fonts, timezone, screen, plugins, etc.
 * Note: This is 100% client-side with no external requests
 *
 * Automatically initializes on module load to ensure fingerprint is ready for use
 */

/**
 * Get the device fingerprint synchronously
 * Returns the cached fingerprint from localStorage or 'pending' if not yet generated
 * Fingerprint generation starts automatically when this module loads
 */
export function getDeviceFingerprint(): string {
  const stored = localStorage.getItem('deviceFingerprint');
  if (stored) {
    return stored;
  }
  return 'pending';
}

// Shared promise to prevent concurrent fingerprint generation
let fingerprintPromise: Promise<void> | null = null;

function ensureFingerprintGenerated(): Promise<void> {
  if (localStorage.getItem('deviceFingerprint')) {
    // Already generated
    return Promise.resolve();
  }
  if (!fingerprintPromise) {
    fingerprintPromise = (async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        localStorage.setItem('deviceFingerprint', result.visitorId);
      } catch (error) {
        console.error('Error generating fingerprint:', error);
        // Fallback to simple fingerprint if FingerprintJS fails
        const fallback = [
          navigator.userAgent,
          navigator.language,
          new Date().getTimezoneOffset().toString(),
          window.screen.width + 'x' + window.screen.height,
          window.screen.colorDepth.toString(),
        ].join('|');
        localStorage.setItem('deviceFingerprint', btoa(fallback).slice(0, 64));
      }
    })();
  }
  return fingerprintPromise;
}

// Auto-initialize fingerprint generation on module load. Deliberately not
// awaited: this is module scope, and a top-level await would both block the
// bundle's evaluation and exceed the browser build target.
void ensureFingerprintGenerated();
